// REST API surface.
//
// Phase 1A — these are MOCKED so the dashboard can be developed
// end-to-end without the backend wired. Each function returns a
// promise that resolves after a small delay with plausible data.
//
// Phase 1C — replace the mock bodies with authFetch calls to the
// real backend (see plan in ~/.claude/plans/partitioned-doodling-flask.md
// section "Wire formats / REST"). Function signatures stay identical.

import type {
  AttentionSummary,
  Company,
  ContextUsage,
  CompactionState,
  Followup,
  HelperDetail,
  HelperList,
  HelperState,
  SessionStatus,
  UIMessage,
} from "../lib/types";
import { authFetch } from "../auth-context";
import type { ChatActivity } from "../lib/sidebarModel";
import { parseCurlOrJson } from "../lib/curl-parser";
import { ASTRA_SCOPE_ERRORS } from "../lib/sendFailure";
import { parseReviewDraft } from "../lib/reviewReferences";
import { parseHelperDetail, parseHelperList } from "../lib/helperPresentation";
import type { ServerMessageState, ServerQueuedItem } from "./delivery";

/** Proxy/gateway statuses that mean "the app never answered", not "the app said no". */
export function isUnconfirmedHttpStatus(status: number): boolean {
  return status === 0 || status === 499 || status === 502 || status === 503 || status === 504 || status === 524;
}

/** A thrown fetch error with no HTTP status (offline, DNS, reset, abort, iOS "Load failed"). */
export function isNetworkLevelError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const name = (err as { name?: unknown }).name;
  return err instanceof TypeError || name === "TypeError" || name === "AbortError" || name === "NetworkError";
}

/* =========================================================================
   Mock toggle — flipped false in Phase 1C now that real backend exists.
   Set to true again to develop UI changes without the backend running.
   ========================================================================= */
const USE_MOCKS = false;

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function slug(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 40);
}

/* =========================================================================
   Mock in-memory store (Phase 1A only) — survives only while the tab is open
   ========================================================================= */
const mockCompanies: Map<string, Company> = new Map();
const mockMessages: Map<string, UIMessage[]> = new Map();
let mockSessionStatus: SessionStatus = {
  companyId: null,
  pid: null,
  state: "idle",
};

// Seed one company so the user can immediately try the flow.
(function seedMocks() {
  const id = "test-co";
  mockCompanies.set(id, {
    id,
    name: "Test Co",
    createdAt: new Date(Date.now() - 86_400_000 * 2).toISOString(),
    lastActiveAt: new Date(Date.now() - 60_000 * 30).toISOString(),
    apiBase: "https://test.reputifly.cloud/wp-json/reputifly/v1/agent",
  });
  mockMessages.set(id, []);
})();

/* =========================================================================
   Companies
   ========================================================================= */
export async function getCompanies(): Promise<Company[]> {
  if (USE_MOCKS) {
    await delay(150);
    return Array.from(mockCompanies.values());
  }
  const r = await authFetch("/api/companies");
  if (!r.ok) throw new Error(`getCompanies HTTP ${r.status}`);
  const j = await r.json();
  return j.companies;
}

export async function addCompany(name: string, curlOrJson: string): Promise<Company> {
  if (USE_MOCKS) {
    await delay(450);
    const parsed = parseCurlOrJson(curlOrJson);
    if (!parsed.ok) throw new Error(parsed.error ?? "parse_failed");
    const id = slug(name) || `c-${Date.now()}`;
    if (mockCompanies.has(id)) throw new Error("company_id_exists");
    const c: Company = {
      id,
      name: name.trim(),
      createdAt: new Date().toISOString(),
      lastActiveAt: new Date().toISOString(),
      apiBase: parsed.value!.apiBase,
    };
    mockCompanies.set(id, c);
    mockMessages.set(id, []);
    return c;
  }
  const r = await authFetch("/api/companies", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, curlOrJson }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error ?? `addCompany HTTP ${r.status}`);
  }
  return (await r.json()).company;
}

/** Create a demo/sample workspace — no key paste, no probe (server skips both). */
export async function addSample(name: string): Promise<Company> {
  if (USE_MOCKS) {
    await delay(300);
    const id = slug(name) || `sample-${Date.now()}`;
    const c: Company = {
      id,
      name: name.trim() || "New sample",
      createdAt: new Date().toISOString(),
      lastActiveAt: new Date().toISOString(),
      apiBase: "",
      kind: "sample",
    };
    mockCompanies.set(id, c);
    mockMessages.set(id, []);
    return c;
  }
  const r = await authFetch("/api/companies/sample", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error ?? `addSample HTTP ${r.status}`);
  }
  return (await r.json()).company;
}

/** Sidebar recency, previews and pin / archive state for every chat (9 Oct 2026). */
export async function getChatActivity(): Promise<Record<string, ChatActivity>> {
  const r = await authFetch("/api/companies/activity");
  if (!r.ok) throw new Error(`getChatActivity HTTP ${r.status}`);
  const body = await r.json();
  const chats = body && typeof body.chats === "object" && body.chats ? body.chats : {};
  const out: Record<string, ChatActivity> = {};
  for (const [id, raw] of Object.entries(chats as Record<string, Partial<ChatActivity>>)) {
    if (!raw || typeof raw !== "object") continue;
    out[id] = {
      at: typeof raw.at === "number" && Number.isFinite(raw.at) ? raw.at : null,
      role: raw.role === "user" || raw.role === "assistant" ? raw.role : null,
      text: typeof raw.text === "string" ? raw.text : null,
      archivedAt: typeof raw.archivedAt === "string" && raw.archivedAt ? raw.archivedAt : null,
      pinnedAt: typeof raw.pinnedAt === "string" && raw.pinnedAt ? raw.pinnedAt : null,
    };
  }
  return out;
}

/** Pin / unpin, archive / unarchive a chat. Only that sidebar flag changes. */
export async function setChatFlag(id: string, flag: "archived" | "pinned", on: boolean, source?: string, pinnedAt?: string | null): Promise<Company> {
  const r = await authFetch(`/api/companies/${encodeURIComponent(id)}/${flag === "archived" ? "archive" : "pin"}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(source ? { "X-UI-Source": source } : {}) },
    // pinnedAt: Undo only — the old pin time, so the chat returns to its place in Pinned.
    body: JSON.stringify(flag === "pinned" && on && pinnedAt ? { [flag]: on, pinnedAt } : { [flag]: on }),
  });
  if (!r.ok) throw new Error(`${flag} HTTP ${r.status}`);
  return (await r.json()).company;
}

export async function renameCompany(id: string, name: string): Promise<Company> {
  if (USE_MOCKS) {
    await delay(200);
    const c = mockCompanies.get(id);
    if (!c) throw new Error("not_found");
    const next = { ...c, name: name.trim() };
    mockCompanies.set(id, next);
    return next;
  }
  const r = await authFetch(`/api/companies/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!r.ok) throw new Error(`renameCompany HTTP ${r.status}`);
  return (await r.json()).company;
}

/** Set the per-chat reasoning effort (low | medium | high | xhigh | max | ultra) for a company. The
 *  server applies it to that chat's NEXT spawned session (normal sends AND
 *  auto-revisions). Returns the updated company (with effortLevel). */
export async function setEffort(
  id: string,
  level: "low" | "medium" | "high" | "xhigh" | "max" | "ultra",
): Promise<Company> {
  if (USE_MOCKS) {
    await delay(120);
    const c = mockCompanies.get(id);
    if (!c) throw new Error("not_found");
    const next = { ...c, effortLevel: level };
    mockCompanies.set(id, next);
    return next;
  }
  const r = await authFetch(`/api/companies/${encodeURIComponent(id)}/effort`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ level }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error ?? `setEffort HTTP ${r.status}`);
  }
  return (await r.json()).company;
}

/** Read-only context-window snapshot for the composer meter. null when the
 *  chat has not reported one yet (404) — the meter then hides itself. */
export async function getContextUsage(companyId: string): Promise<ContextUsage | null> {
  if (USE_MOCKS) return null;
  const r = await authFetch(`/api/sessions/${encodeURIComponent(companyId)}/context`);
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`getContextUsage HTTP ${r.status}`);
  return ((await r.json()).context ?? null) as ContextUsage | null;
}

/** A helper-watcher request the server answered with an error status (Ops has
 *  no shared ApiRequestError; the Helpers sheet only needs the HTTP status). */
export class HelperRequestError extends Error {
  readonly httpStatus: number;
  constructor(message: string, httpStatus: number) {
    super(message);
    this.name = "HelperRequestError";
    this.httpStatus = httpStatus;
  }
}

/** GET /:cid/helpers — every helper (sub-agent) of this chat from the last 2 h,
 *  plus its workflow runs. Fetched only while the Helpers sheet is open. */
export async function getHelpers(companyId: string): Promise<HelperList> {
  if (USE_MOCKS) return { alive: false, helpers: [], runs: [] };
  const r = await authFetch(
    `/api/sessions/${encodeURIComponent(companyId)}/helpers`,
    { cache: "no-store" },
  );
  if (!r.ok) throw new HelperRequestError(`getHelpers HTTP ${r.status}`, r.status);
  return parseHelperList(await r.json());
}

/* Cost breakdown receipt (28 Sep): the latest task's tokens and cost, per agent. */
export interface CostUsd { input: number; output: number; cacheWrite5m: number; cacheWrite1h: number; cacheRead: number; webSearches: number; total: number }
export interface CostLine {
  input: number; output: number; cacheWrite5m: number; cacheWrite1h: number; cacheRead: number; webSearches: number;
  usd: CostUsd; unpricedCalls: number; unpricedModels: string[]; calls: number;
}
export interface CostEntry { id: string; kind: "main" | "agent" | "workflow"; run?: string; name: string; state?: HelperState; bot?: number; cost: CostLine }
export interface TaskCost {
  available: boolean; partial?: boolean; startedAt?: string; lastAt?: string; prompt?: string;
  total: CostLine; entries: CostEntry[]; usdToSgd: number; model: string;
}
/** From when this chat's replies can be priced (ms), or null: older replies hide the button. */
export async function getCostCoverage(companyId: string): Promise<number | null> {
  if (USE_MOCKS) return null;
  const r = await authFetch(`/api/sessions/${encodeURIComponent(companyId)}/cost/coverage`, { cache: "no-store" });
  if (!r.ok) return null;
  const v = Number((await r.json())?.since);
  return Number.isFinite(v) && v > 0 ? v : null;
}
export async function getTaskCost(companyId: string, at?: number | null): Promise<TaskCost> {
  if (USE_MOCKS) return { available: false, total: { input: 0, output: 0, cacheWrite5m: 0, cacheWrite1h: 0, cacheRead: 0, webSearches: 0, usd: { input: 0, output: 0, cacheWrite5m: 0, cacheWrite1h: 0, cacheRead: 0, webSearches: 0, total: 0 }, unpricedCalls: 0, unpricedModels: [], calls: 0 }, entries: [], usdToSgd: 1.3, model: "claude-opus-5-5" };
  const r = await authFetch(`/api/sessions/${encodeURIComponent(companyId)}/cost${at ? `?at=${Math.round(at)}` : ""}`, { cache: "no-store" });
  if (!r.ok) throw new Error(`getTaskCost HTTP ${r.status}`);
  return (await r.json()) as TaskCost;
}

/** GET /:cid/helpers/:id — one helper's brief, steps (from `after`) and result. */
export async function getHelperDetail(
  companyId: string,
  helperId: string,
  opts: { run?: string; after?: number; since?: string } = {},
): Promise<HelperDetail> {
  const query = new URLSearchParams();
  if (opts.run) query.set("run", opts.run);
  if (opts.after) query.set("after", String(opts.after));
  if (opts.since) query.set("since", opts.since);
  const qs = query.toString();
  const r = await authFetch(
    `/api/sessions/${encodeURIComponent(companyId)}/helpers/${encodeURIComponent(helperId)}${qs ? `?${qs}` : ""}`,
    { cache: "no-store" },
  );
  if (!r.ok) throw new HelperRequestError(`getHelperDetail HTTP ${r.status}`, r.status);
  const detail = parseHelperDetail(await r.json());
  if (!detail) throw new HelperRequestError("getHelperDetail: bad reply", 502);
  return detail;
}

/** Server-owned native Compact state, read from the same /context route (its
 *  404 body carries it too). null when an older backend has no such field. */
export async function getCompactionState(companyId: string): Promise<CompactionState | null> {
  if (USE_MOCKS) return null;
  const r = await authFetch(`/api/sessions/${encodeURIComponent(companyId)}/context`);
  if (!r.ok && r.status !== 404) throw new Error(`getCompactionState HTTP ${r.status}`);
  const body = await r.json().catch(() => null);
  const state = body?.compaction;
  return state && typeof state.state === "string" ? (state as CompactionState) : null;
}

/** Start native Compact. Resolves at once with the running state (202); a
 *  refusal throws CompactRefusedError carrying the server's plain reason. The
 *  verdict comes later from server state, never from this request. */
export class CompactRefusedError extends Error {
  readonly code: string;
  constructor(code: string, message: string) { super(message); this.code = code; }
}
export async function compactSession(companyId: string): Promise<CompactionState> {
  const r = await authFetch(`/api/sessions/${encodeURIComponent(companyId)}/compact`, { method: "POST" });
  const body = await r.json().catch(() => null);
  if (r.ok && body?.compaction) return body.compaction as CompactionState;
  if (r.status === 409 || r.status === 404 || r.status === 400) {
    // 404 from a backend without this route: say so plainly.
    const message = typeof body?.message === "string" ? body.message
      : typeof body?.detail === "string" ? body.detail
      : r.status === 404 ? "Compact isn't available on this server yet." : "Compact is not possible right now.";
    throw new CompactRefusedError(String(body?.error ?? `http_${r.status}`), message);
  }
  throw new Error(`compact HTTP ${r.status}`);
}

/** Convert a sample into a real client (kind: sample -> client). Same row —
 *  keeps id, key, api_base, cloak state, and chat history; only the tab changes. */
export async function promoteToClient(id: string): Promise<Company> {
  if (USE_MOCKS) {
    await delay(150);
    const c = mockCompanies.get(id);
    if (!c) throw new Error("not_found");
    const next = { ...c, kind: "client" as const };
    mockCompanies.set(id, next);
    return next;
  }
  const r = await authFetch(`/api/companies/${encodeURIComponent(id)}/promote`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.detail ?? j.error ?? `promoteToClient HTTP ${r.status}`);
  }
  return (await r.json()).company;
}

/** GLOBAL defaults only — deliberately EXCLUDES "ultra". ULTRA is a per-chat,
 *  samples-only tier you opt a single sample into; it must never become the default
 *  for new samples, for clients, or for auto-approved revisions. */
type EffortLvl = "low" | "medium" | "high" | "xhigh" | "max";
export interface GlobalSettings {
  /** Baked into NEW samples at creation (existing samples unaffected). */
  sampleDefaultEffort: EffortLvl | "ultra";
  /** LIVE default for clients — your interactive sends. */
  clientDefaultEffort: EffortLvl;
  /** LIVE default for auto-approved revisions (separate knob). */
  autoRevisionDefaultEffort: EffortLvl;
}

/** Global (dashboard-wide) settings. */
export async function getGlobalSettings(): Promise<GlobalSettings> {
  if (USE_MOCKS) {
    await delay(60);
    return { sampleDefaultEffort: "high", clientDefaultEffort: "xhigh", autoRevisionDefaultEffort: "xhigh" };
  }
  const r = await authFetch(`/api/settings`);
  if (!r.ok) throw new Error(`getGlobalSettings HTTP ${r.status}`);
  return (await r.json()).settings;
}

/** Set the default effort baked into NEW samples (low | medium | high | xhigh | max). */
/** Samples may be set to ULTRA; the client/auto-rev setters keep EffortLvl. */
export async function setSampleDefaultEffort(level: EffortLvl | "ultra"): Promise<GlobalSettings> {
  return postSetting("sample-default-effort", level);
}

/** Set the LIVE default effort for clients (interactive sends). */
export async function setClientDefaultEffort(level: EffortLvl): Promise<GlobalSettings> {
  return postSetting("client-default-effort", level);
}

/** Set the LIVE default effort for auto-approved revisions. */
export async function setAutoRevisionDefaultEffort(level: EffortLvl): Promise<GlobalSettings> {
  return postSetting("auto-revision-default-effort", level);
}

/** Set the per-client effort for AUTO-APPROVED revisions on one client. */
export async function setAutoRevisionEffort(id: string, level: EffortLvl): Promise<Company> {
  if (USE_MOCKS) {
    await delay(120);
    const c = mockCompanies.get(id);
    if (!c) throw new Error("not_found");
    const next = { ...c, autoRevisionEffort: level };
    mockCompanies.set(id, next);
    return next;
  }
  const r = await authFetch(`/api/companies/${encodeURIComponent(id)}/auto-revision-effort`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ level }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error ?? `setAutoRevisionEffort HTTP ${r.status}`);
  }
  return (await r.json()).company;
}

async function postSetting(path: string, level: EffortLvl | "ultra"): Promise<GlobalSettings> {
  if (USE_MOCKS) {
    await delay(120);
    return { sampleDefaultEffort: "high", clientDefaultEffort: "xhigh", autoRevisionDefaultEffort: level as EffortLvl };
  }
  const r = await authFetch(`/api/settings/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ level }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error ?? `${path} HTTP ${r.status}`);
  }
  return (await r.json()).settings;
}

export async function rotateKey(id: string, curlOrJson: string): Promise<Company> {
  if (USE_MOCKS) {
    await delay(550);
    const parsed = parseCurlOrJson(curlOrJson);
    if (!parsed.ok) throw new Error(parsed.error ?? "parse_failed");
    const c = mockCompanies.get(id);
    if (!c) throw new Error("not_found");
    const next = { ...c, apiBase: parsed.value!.apiBase };
    mockCompanies.set(id, next);
    return next;
  }
  const r = await authFetch(`/api/companies/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ curlOrJson }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error ?? `rotateKey HTTP ${r.status}`);
  }
  return (await r.json()).company;
}

// Re-point a company to a new domain while KEEPING its existing key (the common
// migration case: the site moved, the key didn't). Backend reuses the stored key
// and probes the new domain before saving.
export async function changeDomain(id: string, newApiBase: string): Promise<Company> {
  if (USE_MOCKS) {
    await delay(500);
    const c = mockCompanies.get(id);
    if (!c) throw new Error("not_found");
    const next = { ...c, apiBase: newApiBase };
    mockCompanies.set(id, next);
    return next;
  }
  const r = await authFetch(`/api/companies/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ newApiBase }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.detail ?? j.error ?? `changeDomain HTTP ${r.status}`);
  }
  return (await r.json()).company;
}

export async function deleteCompany(id: string): Promise<void> {
  if (USE_MOCKS) {
    await delay(400);
    mockCompanies.delete(id);
    mockMessages.delete(id);
    if (mockSessionStatus.companyId === id) {
      mockSessionStatus = { companyId: null, pid: null, state: "idle" };
    }
    return;
  }
  const r = await authFetch(`/api/companies/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!r.ok) throw new Error(`deleteCompany HTTP ${r.status}`);
}

/* =========================================================================
   Sessions
   ========================================================================= */
export async function getSessionStatus(): Promise<SessionStatus> {
  if (USE_MOCKS) {
    await delay(80);
    return { ...mockSessionStatus };
  }
  const r = await authFetch("/api/sessions/status");
  if (!r.ok) throw new Error(`getSessionStatus HTTP ${r.status}`);
  return (await r.json()).status;
}

/**
 * Multi-session-aware status. Returns map of every companyId currently
 * with an active (spawning/running/killing/errored) session. Idle companies
 * are omitted from the response. Polled every ~6s by the store to drive
 * sidebar pulsing dots + topbar active-session count chip.
 */
export async function getAllSessionStatuses(): Promise<Record<string, SessionStatus>> {
  if (USE_MOCKS) {
    await delay(40);
    if (mockSessionStatus.state === "idle" || !mockSessionStatus.companyId) return {};
    return { [mockSessionStatus.companyId]: { ...mockSessionStatus } };
  }
  const r = await authFetch("/api/sessions/all-status");
  if (!r.ok) throw new Error(`getAllSessionStatuses HTTP ${r.status}`);
  const body = await r.json();
  _lastUnread = body.unread && typeof body.unread === "object" ? body.unread : null;
  _lastManualUnread = body.manualUnread && typeof body.manualUnread === "object" ? body.manualUnread : null;
  return body.statuses ?? {};
}

/* Unread replies (28 Sep): the same poll carries companyId → reply time for chats whose
 * newest reply to the operator hasn't been opened on any device. */
let _lastUnread: Record<string, number> | null = null;
export function lastPolledUnread(): Record<string, number> | null { return _lastUnread; }
/* Chats marked unread by hand (C9, 9 Oct): their own map, so a mark is a dot only — never a
 * reply time that would move the row or change its time. null from an older backend. */
let _lastManualUnread: Record<string, number> | null = null;
export function lastPolledManualUnread(): Record<string, number> | null { return _lastManualUnread; }
export async function markChatRead(companyId: string): Promise<void> {
  if (USE_MOCKS) return;
  await authFetch(`/api/sessions/${encodeURIComponent(companyId)}/read`, { method: "POST" });
}
/** "Mark as unread": the chat shows unread on every device until someone opens it. */
export async function markChatUnread(companyId: string, source?: string): Promise<void> {
  if (USE_MOCKS) return;
  const r = await authFetch(`/api/sessions/${encodeURIComponent(companyId)}/unread`, {
    method: "POST", headers: source ? { "X-UI-Source": source } : undefined,
  });
  if (!r.ok) throw new Error(`unread HTTP ${r.status}`);
}

/**
 * Server health for the topbar RAM/load chip. Cheap — reads /proc/meminfo
 * + /proc/loadavg server-side. Polled every ~8s.
 */
export interface ServerMetrics {
  ram: { totalMB: number; usedMB: number; usedPct: number };
  load: { one: number; five: number; fifteen: number };
  sessions: { active: number };
}
export async function getMetrics(): Promise<ServerMetrics> {
  if (USE_MOCKS) {
    await delay(20);
    return {
      ram: { totalMB: 16384, usedMB: 1100, usedPct: 7 },
      load: { one: 0.05, five: 0.1, fifteen: 0.12 },
      sessions: { active: 0 },
    };
  }
  const r = await authFetch("/api/metrics");
  if (!r.ok) throw new Error(`getMetrics HTTP ${r.status}`);
  return await r.json();
}

export async function spawnSession(companyId: string): Promise<SessionStatus> {
  if (USE_MOCKS) {
    // Simulate: if a different company has an active session, kill first.
    if (mockSessionStatus.companyId && mockSessionStatus.companyId !== companyId) {
      mockSessionStatus = { ...mockSessionStatus, state: "killing" };
      await delay(450);
    }
    mockSessionStatus = { companyId, pid: null, state: "spawning" };
    await delay(900);
    mockSessionStatus = {
      companyId,
      pid: 10_000 + Math.floor(Math.random() * 10_000),
      state: "running",
      spawnedAt: new Date().toISOString(),
    };
    return { ...mockSessionStatus };
  }
  const r = await authFetch(
    `/api/sessions/${encodeURIComponent(companyId)}/spawn`,
    { method: "POST" },
  );
  if (!r.ok) throw new Error(`spawnSession HTTP ${r.status}`);
  return (await r.json()).status;
}

/** Restart a stuck/crashed agent for one company. Kills claude, drains
 *  the queue, and DELETES the .jsonl on disk so the next spawn starts
 *  with empty context. SQLite chat history + notes survive. */
export async function restartCompanySession(
  companyId: string,
): Promise<{ jsonlDeleted: boolean }> {
  if (USE_MOCKS) {
    mockSessionStatus = { companyId: null, pid: null, state: "idle" };
    return { jsonlDeleted: false };
  }
  const r = await authFetch(
    `/api/sessions/${encodeURIComponent(companyId)}/restart`,
    { method: "POST" },
  );
  if (!r.ok) {
    const txt = await r.text().catch(() => "");
    throw new Error(`restartCompanySession HTTP ${r.status}: ${txt.slice(0, 120)}`);
  }
  const body = (await r.json()) as { ok: true; jsonlDeleted: boolean };
  return { jsonlDeleted: body.jsonlDeleted };
}

export async function killActiveSession(): Promise<SessionStatus> {
  if (USE_MOCKS) {
    if (mockSessionStatus.state === "idle") return { ...mockSessionStatus };
    mockSessionStatus = { ...mockSessionStatus, state: "killing" };
    await delay(450);
    mockSessionStatus = { companyId: null, pid: null, state: "idle" };
    return { ...mockSessionStatus };
  }
  const r = await authFetch("/api/sessions/active", { method: "DELETE" });
  if (!r.ok) throw new Error(`killActiveSession HTTP ${r.status}`);
  return (await r.json()).status;
}

export async function interruptCurrentTurn(companyId: string): Promise<void> {
  if (USE_MOCKS) {
    // sse.ts abortMockStream is what actually stops the mock stream;
    // backend interrupt is a real HTTP call in 1C.
    await delay(100);
    return;
  }
  const r = await authFetch(
    `/api/sessions/${encodeURIComponent(companyId)}/interrupt`,
    { method: "POST" },
  );
  if (r.status === 409) {
    const body = await r.json().catch(() => null);
    if (body?.error === "compacting") throw new CompactRefusedError("compacting", String(body.message ?? "Compacting context — this finishes on its own in a moment."));
  }
  if (!r.ok) throw new Error(`interrupt HTTP ${r.status}`);
}

/** Cancel ONE still-waiting queued message (the ✕ on a pending bubble).
 *  Returns { cancelled, removed } — cancelled:false if it already started
 *  draining (gone from the server queue) or never existed; removed:true means
 *  the server recorded the id as removed, so it is never delivered later (also
 *  for a chip whose POST the server never received). The backend route is
 *  additive and never touches the in-flight turn. */
export async function sendNativeInputNow(companyId: string, userMsgId: string): Promise<NativeInputResponse> {
  const r = await authFetch(`/api/sessions/${encodeURIComponent(companyId)}/queue/${encodeURIComponent(userMsgId)}/send-now`, { method: "POST" });
  if (!r.ok) throw new Error(`Send now HTTP ${r.status}`);
  return r.json();
}

export async function cancelQueuedMessage(
  companyId: string,
  userMsgId: string,
): Promise<{ cancelled: boolean; removed?: boolean } & NativeInputResponse> {
  if (USE_MOCKS) return { cancelled: true };
  const r = await authFetch(
    `/api/sessions/${encodeURIComponent(companyId)}/queue/${encodeURIComponent(userMsgId)}`,
    { method: "DELETE" },
  );
  if (!r.ok) {
    const txt = await r.text().catch(() => "");
    throw new Error(`cancelQueuedMessage HTTP ${r.status}: ${txt.slice(0, 120)}`);
  }
  return (await r.json()) as { cancelled: boolean; removed?: boolean };
}

/** Edit a message that has not been sent yet (same id, same place in line).
 *  Never throws for a server answer: 409 = already sent / removed (the race). */
export async function editQueuedMessage(
  companyId: string,
  userMsgId: string,
  text: string,
): Promise<{ status: number; body: { edited?: boolean; error?: string; detail?: string; position?: number } & NativeInputResponse }> {
  if (USE_MOCKS) return { status: 200, body: { edited: true } as any };
  const r = await authFetch(
    `/api/sessions/${encodeURIComponent(companyId)}/queue/${encodeURIComponent(userMsgId)}`,
    { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) },
  );
  const body = await r.json().catch(() => ({}));
  return { status: r.status, body };
}

/** Keep a native input out of the agent's hands while its editor is open. */
export async function holdQueuedMessage(companyId: string, userMsgId: string, editing: boolean): Promise<{ held?: boolean } & NativeInputResponse> {
  if (USE_MOCKS) return { held: false } as any;
  const r = await authFetch(
    `/api/sessions/${encodeURIComponent(companyId)}/queue/${encodeURIComponent(userMsgId)}/hold`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ editing }) },
  );
  if (!r.ok) throw new Error(`hold HTTP ${r.status}`);
  return r.json();
}

/** Reveal the bearer key + api_base for a single company so the user
 *  can build a curl elsewhere (Telegram bot, etc). Auth-gated. The
 *  response is never cached and should only be requested in response to
 *  a direct user action (click "Copy curl"). */
export async function revealCompanyKey(
  companyId: string,
): Promise<{ name: string; apiBase: string; agentKey: string }> {
  if (USE_MOCKS) {
    return { name: companyId, apiBase: "https://example.com", agentKey: "mock_key" };
  }
  const r = await authFetch(
    `/api/companies/${encodeURIComponent(companyId)}/reveal-key`,
  );
  if (!r.ok) {
    const txt = await r.text().catch(() => "");
    throw new Error(`revealCompanyKey HTTP ${r.status}: ${txt.slice(0, 120)}`);
  }
  return await r.json();
}

/** Wipe SQLite messages + claude session jsonl for this company.
 *  Preserves notes/<company>.md (the agent's persistent memory).
 *  Next spawn for this company starts with an empty context window. */
export async function clearChatHistory(companyId: string): Promise<{ cleared: number; nativeInput?: NativeInputSnapshot }> {
  if (USE_MOCKS) {
    // Mock backend has no separate clear endpoint — store-level reset
    // handles the UI wipe. Server-side mock state goes stale here, but
    // that's fine for the dev shell.
    return { cleared: 0 };
  }
  const r = await authFetch(
    `/api/sessions/${encodeURIComponent(companyId)}/clear-history`,
    { method: "POST" },
  );
  if (!r.ok) {
    const txt = await r.text().catch(() => "");
    throw new Error(`clearChatHistory HTTP ${r.status}: ${txt.slice(0, 120)}`);
  }
  const body = (await r.json()) as { ok: true; cleared: number; nativeInput?: NativeInputSnapshot };
  return { cleared: body.cleared, nativeInput: body.nativeInput };
}

/* =========================================================================
   Revisions — "Attention → Approve" flow
   ========================================================================= */

/** Cached per-company pending-revision summaries (polled ~6s alongside status). */
export async function getAttention(): Promise<Record<string, AttentionSummary>> {
  if (USE_MOCKS) {
    await delay(30);
    return {};
  }
  const r = await authFetch("/api/revision/attention");
  if (!r.ok) throw new Error(`getAttention HTTP ${r.status}`);
  return (await r.json()).attention ?? {};
}

/** Julian-approved dispatch. threadId omitted ⇒ work ALL pending for the client.
 *  excludeGated ⇒ dispatch only in-scope items, never approve a build still gated
 *  on Julian (used by the one-click Auto-approve ON sweep). */
export async function runRevision(
  companyId: string,
  threadId?: number | null,
  opts?: { excludeGated?: boolean; note?: string },
): Promise<{ status: string }> {
  if (USE_MOCKS) return { status: "dispatched" };
  const body: Record<string, unknown> = { companyId };
  if (threadId != null) body.threadId = threadId;
  if (opts?.excludeGated) body.excludeGated = true;
  if (opts?.note && opts.note.trim()) body.note = opts.note;
  const r = await authFetch("/api/revision/run", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.detail ?? j.error ?? `runRevision HTTP ${r.status}`);
  }
  return await r.json();
}

/** Terminal "skip it": decline a gated build + resolve the thread so it leaves
 *  Attention for good (never re-nags). */
export async function dismissRevision(companyId: string, threadId: number): Promise<{ status: string }> {
  if (USE_MOCKS) return { status: "dismissed" };
  const r = await authFetch("/api/revision/dismiss", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ companyId, threadId }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.detail ?? j.error ?? `dismissRevision HTTP ${r.status}`);
  }
  return await r.json();
}

/** Opt a client in/out of AUTO-approve. Returns the updated company. */
export async function setAutomationFreeze(companyId: string, frozen: boolean, reviewedPending = false): Promise<Company> {
  if (!frozen && !reviewedPending) throw new Error('Review pending work and schedules before lifting the safety hold.');
  if (USE_MOCKS) {
    const company = mockCompanies.get(companyId)!;
    const next = { ...company, automationFrozen: frozen };
    mockCompanies.set(companyId, next); return next;
  }
  const response = await authFetch(`/api/companies/${encodeURIComponent(companyId)}/automation-freeze`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ frozen, reviewedPending }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.detail ?? data.error ?? 'Could not save automation setting');
  return data.company;
}

export async function setAutoRevision(companyId: string, enabled: boolean): Promise<Company> {
  if (USE_MOCKS) {
    const c = mockCompanies.get(companyId);
    if (c) mockCompanies.set(companyId, { ...c, autoRevision: enabled });
    return mockCompanies.get(companyId)!;
  }
  const r = await authFetch("/api/revision/auto", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ companyId, enabled }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.detail ?? j.error ?? `setAutoRevision HTTP ${r.status}`);
  }
  return (await r.json()).company;
}

/** Opt a client in/out of the Attention watch. Returns the updated company. */
export async function setRevisionsEnabled(
  companyId: string,
  enabled: boolean,
): Promise<Company> {
  if (USE_MOCKS) {
    const c = mockCompanies.get(companyId);
    if (c) mockCompanies.set(companyId, { ...c, revisionsEnabled: enabled });
    return mockCompanies.get(companyId)!;
  }
  const r = await authFetch("/api/revision/enable", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ companyId, enabled }),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.detail ?? j.error ?? `setRevisionsEnabled HTTP ${r.status}`);
  }
  return (await r.json()).company;
}

/* =========================================================================
   Messages
   ========================================================================= */

/**
 * Backend reply shape from GET /api/sessions/:c/messages.
 * `inFlight` lets the store reconstruct "Claude is currently working" state
 * after a page refresh — without it, refresh during a stream wipes the
 * progress chip + tool bubbles and the UI looks idle. Optional in the type
 * to stay compatible with older backend builds that didn't return the field.
 */
import type { NativeInputSnapshot } from "./nativeInputs";
export interface NativeInputResponse extends Partial<NativeInputSnapshot> { nativeInput?: boolean }

export interface MessageHistoryResponse {
  messages: UIMessage[];
  /** `queued` is the server's waiting list (absent on an older backend). */
  inFlight: { nativeInput?: NativeInputSnapshot; busy: boolean; queueLength: number; queued?: ServerQueuedItem[] };
}

/**
 * A message POST that got NO answer from the app: the network dropped, the
 * phone backgrounded/aborted it, or the proxy answered 499/502/503/504/524.
 * The server may have received (and delivered) it — never report this as a
 * failure; ask the server about the id instead.
 */
export class SendUnconfirmedError extends Error {
  constructor(detail: string) {
    super(detail);
    this.name = "SendUnconfirmedError";
  }
}

/**
 * The message route answered 502 spawn_failed: it could not start this chat (every
 * slot busy and the slot line is off for it, or the start itself failed). Nothing was
 * saved or queued, so this is a definite "not delivered" (unlike a bare proxy 502).
 */
export class StartRefusedError extends Error {
  readonly capacity: boolean;
  constructor(detail: string, capacity: boolean) {
    super(detail);
    this.name = "StartRefusedError";
    this.capacity = capacity;
  }
}

/** What the server knows about one operator message id. */
export async function getMessageStatus(companyId: string, userMsgId: string): Promise<ServerMessageState> {
  if (USE_MOCKS) return "delivered";
  const r = await authFetch(
    `/api/sessions/${encodeURIComponent(companyId)}/messages/${encodeURIComponent(userMsgId)}/status`,
  );
  if (!r.ok) throw new Error(`getMessageStatus HTTP ${r.status}`);
  const body = await r.json().catch(() => null);
  const state = body?.state;
  return state === "delivered" || state === "queued" || state === "pending" || state === "failed" || state === "unknown" || state === "removed"
    ? state
    : "unknown";
}

export async function getMessageHistory(companyId: string, signal?: AbortSignal): Promise<MessageHistoryResponse> {
  if (USE_MOCKS) {
    await delay(120);
    return {
      messages: mockMessages.get(companyId) ?? [],
      inFlight: { busy: false, queueLength: 0 },
    };
  }
  // Bumped 200 → 500 for longer-running clients. Cheap on the wire
  // (SQLite query is indexed by company_id+created_at) and avoids the
  // "scroll back, see nothing, get confused" footgun on heavy chats.
  // Use Compact (context meter / row menu) or Clear chat if it gets too big.
  const r = await authFetch(
    `/api/sessions/${encodeURIComponent(companyId)}/messages?limit=500`,
    { signal },
  );
  if (!r.ok) throw new Error(`getMessageHistory HTTP ${r.status}`);
  const body = await r.json();
  return {
    messages: Array.isArray(body.messages) ? body.messages : [],
    inFlight: {
      nativeInput: body.inFlight?.nativeInput,
      busy: !!body.inFlight?.busy,
      queueLength: Number(body.inFlight?.queueLength ?? 0),
      // Inline (not a helper call) so this function stays self-contained.
      queued: Array.isArray(body.inFlight?.queued)
        ? (body.inFlight.queued as Array<Partial<Record<keyof ServerQueuedItem, unknown>> | null>).flatMap((item): ServerQueuedItem[] =>
            item && typeof item.userMsgId === "string" && typeof item.text === "string"
              ? [{ userMsgId: item.userMsgId, text: item.text, truncated: item.truncated === true,
                  files: Array.isArray(item.files) ? item.files.filter((f: unknown): f is string => typeof f === "string") : [],
                  queuedAt: Number(item.queuedAt) || 0 }]
              : [])
        : undefined,
    },
  };
}

export async function postMessage(
  companyId: string,
  text: string,
  attachmentPaths: string[],
  notifyOnDone = false,
  /**
   * Optimistic frontend ID for the user message. Backend persists with
   * this ID instead of generating its own, so the message keeps the same
   * ID across history reloads. Without this, every resyncActive() / focus
   * event replaces optimistic IDs with server-generated ones, breaking
   * any frontend state that referenced the original ID (specifically:
   * QueuedMessage.userMsgId → UserBubble queued-badge match).
   */
  userMsgId?: string,
): Promise<{ nativeInput?: boolean; rootId?: string | null; inputs?: NativeInputSnapshot["inputs"]; revision?: number; queued: boolean; position: number; waitingForSlot?: boolean; slotPlace?: number; duplicate?: boolean; state?: "delivered" | "queued" | "pending" | "removed" }> {
  if (USE_MOCKS) {
    await delay(80);
    // Mock backend just accepts — the SSE mock layer handles the response.
    return { queued: false, position: 0 };
  }
  const review = parseReviewDraft(text);
  if (review.references.some(reference => reference.companyId !== companyId)) {
    throw new Error("This reference belongs to another client. Open its chat with Discuss before sending; your message is preserved.");
  }
  // Backend expects `attachments: [{ path, kind?, filename? }]`. Sending bare
  // strings here used to silently drop the attachments (backend filter on
  // `a.path` would strip every entry), which is what caused the "no
  // attachment_path" bug Julian hit on 2026-05-25. Derive kind + filename
  // from the upload path so the plugin's meta.attachment_filenames is
  // non-empty too.
  const attachments = attachmentPaths.map((p) => {
    const filename = p.split("/").pop() ?? p;
    const ext = filename.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] ?? "";
    const kind: "image" | "pdf" | "file" =
      ["png", "jpg", "jpeg", "gif", "webp", "heic", "heif"].includes(ext)
        ? "image"
        : ext === "pdf"
          ? "pdf"
          : "file";
    return { path: p, kind, filename };
  });
  const body = JSON.stringify({ text: review.text, reviewReferences: review.references, attachments, notifyOnDone, userMsgId });
  const send = (keepalive: boolean) => authFetch(
    `/api/sessions/${encodeURIComponent(companyId)}/message`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive },
  );
  // The browser keeps a keepalive request going after the page/app closes (Julian, 20 Oct:
  // tap Send, close the app). Browsers cap ALL in-flight keepalive bodies together at 64 KB
  // (bytes, not characters), so long texts go normally. If the browser refuses the keepalive
  // request (TypeError: e.g. a burst used up the 64 KB), it is sent again without it; the
  // server dedupes by userMsgId, so a request that did leave cannot run twice.
  const keepalive = new TextEncoder().encode(body).length < 48_000;
  let r: Response;
  try {
    try { r = await send(keepalive); }
    catch (err) {
      if (!keepalive || !(err instanceof TypeError)) throw err;
      r = await send(false);
    }
  } catch (err) {
    // No HTTP status at all (offline, reset, abort, iOS "Load failed"): the
    // request may or may not have reached the server.
    throw new SendUnconfirmedError(isNetworkLevelError(err) ? "No answer from the server yet" : String((err as Error)?.message ?? err));
  }
  if (!r.ok) {
    // Only translate this route's explicit scope codes. Never display arbitrary
    // backend text (which may contain private paths or provider diagnostics).
    const body = [403, 409, 502, 503].includes(r.status) ? await r.json().catch(() => null) : null;
    // The app's own answer (JSON), not a proxy page: the chat could not be started and
    // nothing was saved. A cold send now POSTs at once, so this is where a refusal lands.
    if (r.status === 502 && body?.error === "spawn_failed") {
      const reason = typeof body?.status?.errorMessage === "string" ? body.status.errorMessage : "";
      const capacity = /at session cap|sub-pool full|waiting for a slot/i.test(reason);
      throw new StartRefusedError(capacity ? "Every session slot is busy" : "The chat could not be started", capacity);
    }
    if (r.status === 403 && body?.error === "review_company_mismatch") {
      throw new Error("This reference belongs to another client. Open its chat with Discuss before sending; your message is preserved.");
    }
    if (r.status === 409 && body?.error === "astra_general_requires_client_chat") {
      throw new Error(ASTRA_SCOPE_ERRORS.astra_general_requires_client_chat);
    }
    if (r.status === 409 && (body?.error === "opus_creative_requires_sample_chat" || body?.error === "compact_unavailable_in_operations" || body?.error === "use_compact_action")) {
      throw new Error(ASTRA_SCOPE_ERRORS[body.error as "opus_creative_requires_sample_chat" | "compact_unavailable_in_operations" | "use_compact_action"]);
    }
    if (r.status === 503 && body?.error === "astra_chat_scope_unavailable") {
      throw new Error(ASTRA_SCOPE_ERRORS.astra_chat_scope_unavailable);
    }
    if (r.status === 409 && ["quotation_changed", "quotation_registration_changed", "revision_changed", "quotation_not_found"].includes(body?.error)) {
      throw new Error("A referenced quotation or revision changed. Reopen review and insert its latest reference; your message is preserved.");
    }
    // The app itself never answered (proxy timeout, restart, spawn/delivery
    // hiccup). Not proof of failure: the id is checked with the server.
    if (isUnconfirmedHttpStatus(r.status)) throw new SendUnconfirmedError(`HTTP ${r.status}`);
    throw new Error(`postMessage HTTP ${r.status}`);
  }
  // A 200 whose body never arrived is still unconfirmed, not failed.
  try {
    return await r.json();
  } catch {
    throw new SendUnconfirmedError("Reply cut off");
  }
}

/* =========================================================================
   Uploads
   ========================================================================= */
export interface UploadResult {
  path: string;
  kind: "image" | "pdf" | "file";
  sizeBytes: number;
}

export async function uploadFile(
  file: File | Blob,
  filename: string,
  companyId?: string,
): Promise<UploadResult> {
  if (USE_MOCKS) {
    await delay(300);
    const kind: UploadResult["kind"] = filename.toLowerCase().endsWith(".pdf")
      ? "pdf"
      : /\.(png|jpe?g|gif|webp)$/i.test(filename)
        ? "image"
        : "file";
    return {
      path: `/home/claude/agent/inbox/mock/${Date.now()}-${filename}`,
      kind,
      sizeBytes: file.size,
    };
  }
  const fd = new FormData();
  // companyId must be appended BEFORE file so multer's diskStorage callback
  // sees it in req.body when computing the destination dir. Multer parses
  // form fields in order; file-field handler reads req.body for prior fields.
  if (companyId) fd.append("companyId", companyId);
  fd.append("file", file, filename);
  const r = await authFetch("/api/uploads", { method: "POST", body: fd });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error ?? `uploadFile HTTP ${r.status}`);
  }
  return await r.json();
}

/* =========================================================================
   Mock-only test hooks (exported so sse.ts mock can read message history)
   ========================================================================= */
export const __mock = USE_MOCKS
  ? {
      appendMessage(companyId: string, m: UIMessage) {
        const arr = mockMessages.get(companyId) ?? [];
        arr.push(m);
        mockMessages.set(companyId, arr);
      },
      patchMessage(companyId: string, msgId: string, patch: Partial<UIMessage>) {
        const arr = mockMessages.get(companyId) ?? [];
        const idx = arr.findIndex((m) => m.id === msgId);
        if (idx >= 0) {
          arr[idx] = { ...arr[idx], ...patch };
          mockMessages.set(companyId, arr);
        }
      },
    }
  : null;

/* =========================================================================
   Scheduled follow-ups (⋯ → Scheduled tasks)

   The backend owns every rule here. `canCancel` arrives ON the row rather than
   being re-derived client-side, so a UI that disagrees with the server is not
   possible; and cancelling an agent-owned row is refused by the ROUTE, so the
   hidden button is a courtesy, not the control.
   ========================================================================= */

export interface FollowupList {
  followups: Followup[];
  /** companyId → display name. Populated for scope "all"; empty for "chat". */
  names: Record<string, string>;
  /** Both counts on EVERY response. A "chat" response has never seen the other
   *  companies' rows, so the other number cannot be derived here — and a count
   *  that only appears after you switch cannot help you decide to switch. */
  totals: { chat: number; all: number };
}

/** scope "chat" needs a companyId; scope "all" spans every client. */
export async function getFollowups(
  scope: "chat" | "all",
  companyId?: string,
): Promise<FollowupList> {
  if (USE_MOCKS) {
    await delay(60);
    return { followups: [], names: {}, totals: { chat: 0, all: 0 } };
  }
  const qs =
    scope === "all"
      ? "scope=all"
      : `scope=chat&companyId=${encodeURIComponent(companyId ?? "")}`;
  const r = await authFetch(`/api/followups?${qs}`);
  if (!r.ok) throw new Error(`getFollowups HTTP ${r.status}`);
  return (await r.json()) as FollowupList;
}

/** Arm one. Body mirrors the spool's own shape so the backend can hand it
 *  straight to the same validate() the agent's file drops go through. */
export async function createFollowup(input: {
  companyId: string;
  prompt: string;
  reason?: string;
  delayMs: number;
  everyMs?: number | null;
  maxRuns?: number | null;
  backoff?: boolean;
  deadlineMs?: number | null;
}): Promise<Followup> {
  if (USE_MOCKS) {
    await delay(60);
    throw new Error("createFollowup is not mocked");
  }
  const r = await authFetch("/api/followups", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.detail ?? j.error ?? `createFollowup HTTP ${r.status}`);
  }
  return (await r.json()).followup as Followup;
}

/** Cancel. 400 `agent_owned` is the expected refusal for an agent's own row —
 *  its `detail` is written for a human, so surface it verbatim in the toast. */
export async function cancelFollowup(id: string): Promise<Followup | null> {
  if (USE_MOCKS) return null;
  const r = await authFetch(`/api/followups/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.detail ?? j.error ?? `cancelFollowup HTTP ${r.status}`);
  }
  return (await r.json()).followup ?? null;
}

/** Bring the next run forward to now. The server moves next_fire_at and the
 *  scheduler's own tick does the firing within 30s — nothing dispatches here. */
export async function runFollowupNow(id: string): Promise<Followup | null> {
  if (USE_MOCKS) return null;
  const r = await authFetch(`/api/followups/${encodeURIComponent(id)}/run`, { method: "POST" });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.detail ?? j.error ?? `runFollowupNow HTTP ${r.status}`);
  }
  return (await r.json()).followup ?? null;
}

/** Delete FINISHED rows only — armed schedules are untouchable here and in the
 *  route. Omitting companyId clears every chat's history. */
export async function clearFinishedFollowups(companyId?: string): Promise<number> {
  if (USE_MOCKS) return 0;
  const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : "";
  const r = await authFetch(`/api/followups/finished${qs}`, { method: "DELETE" });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.detail ?? j.error ?? `clearFinishedFollowups HTTP ${r.status}`);
  }
  return (await r.json()).cleared ?? 0;
}

/* Agent-first scheduling. Browser management remains Firebase-authenticated;
 * external bearer keys are never installed into the app's global auth/store. */
export class SchedulingApiError extends Error {
  readonly status: number;
  constructor(status: number) {
    super(status === 409 ? "This changed elsewhere. Refresh and try again."
      : status === 401 || status === 403 ? "Access denied. Sign in again or refresh this chat."
      : status === 404 ? "This chat or schedule is no longer available."
      : "The scheduling request could not be confirmed. Refresh before trying again.");
    this.status = status;
  }
}
async function schedulingRequest<T>(path: string, body?: Record<string, unknown>): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new Error("The scheduling request timed out. Refresh before retrying."));
    }, 20000);
  });
  try {
    return await Promise.race([deadline, (async () => {
      const response = await authFetch(path, {
        method: body === undefined ? "GET" : "POST", cache: "no-store", signal: controller.signal,
        ...(body === undefined ? {} : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
      });
      if (!response.ok) throw new SchedulingApiError(response.status);
      return await response.json() as T;
    })()]);
  } finally { if (timer !== undefined) clearTimeout(timer); }
}
export function getSchedulingAccess(chatId: string) {
  return schedulingRequest<{ access: import("../lib/types").SchedulingAccess | null }>(`/api/scheduling/access/${encodeURIComponent(chatId)}`);
}
export function copySchedulingAccess(chatId: string) {
  return schedulingRequest<{ access: import("../lib/types").SchedulingAccess; setup: string }>(`/api/scheduling/access/${encodeURIComponent(chatId)}/copy`, {});
}
export function rotateSchedulingAccess(chatId: string, expectedKeyId: string) {
  return schedulingRequest<{ access: import("../lib/types").SchedulingAccess; setup: string }>(`/api/scheduling/access/${encodeURIComponent(chatId)}/rotate`, { expectedKeyId });
}
export function revokeSchedulingAccess(chatId: string, expectedKeyId: string) {
  return schedulingRequest<{ access: import("../lib/types").SchedulingAccess }>(`/api/scheduling/access/${encodeURIComponent(chatId)}/revoke`, { expectedKeyId });
}
export function getSchedulingPlans(scope: "chat" | "all", companyId?: string) {
  return schedulingRequest<{ plans: import("../lib/types").SchedulingPlan[]; names: Record<string, string>; totals: { chat: number; all: number } }>(
    `/api/scheduling/plans?scope=${scope}${companyId ? `&companyId=${encodeURIComponent(companyId)}` : ""}`,
  );
}
export function schedulingCommand<T>(chatId: string, command: Record<string, unknown>): Promise<T> {
  return schedulingRequest<T>(`/api/scheduling/chats/${encodeURIComponent(chatId)}/commands`, command);
}
/** Hide exact finished cards for this signed-in owner on all devices. The server
 * retains schedules/history, and rejects stale or nonterminal selections. */
export function clearFinishedSchedulingPlans(plans: Pick<import("../lib/types").SchedulingPlan, "id"|"chatId"|"epoch"|"version"|"status">[]): Promise<{cleared: number}> {
  return schedulingRequest(`/api/scheduling/plans/clear-finished`, {
    plans: plans.map(({id,chatId,epoch,version,status}) => ({id,chatId,epoch,version,status})),
  });
}
